/**
 * K4a „Erwartung gegen Ist je Bestellquartal" (P2-V3 § 2.6; die nächsten Rechnungen stehen seit F4 in K4b,
 * `NaechsteRechnungenKarte.tsx`). EIN Fundament für Erwarteten Wert, Bisher bezahlt und Anteil: Anfos mit Erwartetem
 * Wert und bekanntem Zahlstand, ohne vorläufige; Ausnahmen tragen eine Marke (¹ ²) mit Fußnote.
 * Zeitbasis (E8-Abgleich 23.10.2026): bewertet wird mit dem HEUTIGEN Wert der Angebote, auch für alte Quartale — der Kopf
 * heißt deshalb „heutiger Angebotswert", kein Text behauptet den damaligen Wert. „Anteil schon bezahlt" nur zusammen mit
 * dem Bündel „Bisher bezahlt" (Kopplung `nurMit`; am Handy zählt das Alter in der Namenszelle als Partner).
 * Veränderung zum Vorjahresquartal aus derselben Backend-Funktion wie die K1-Lesezeile; Hochrechnung (m5) nur Wahl.
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { Legende, TipRow, unsicherFuellung, unsicherKonturen, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { AnfosZelle, BezahltBuendel, Hinweis, int, JungEtikett, Laden, nachGruppenReihenfolge, tEuro, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatEur, formatPct, jungTip, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchPrognoseGeteilt, formatPeriode, PrognoseQuartalP2 } from '../../../helpers/auswertungenData';
import { DatenstandText, Kartenkopf, P2_GRUPPEN, schwellenAus, VeraenderungZelle } from './teile';

const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['q', 'anfos', 'erw', 'bezahlt', 'anteil'],
  mittel: ['q', 'anfos', 'erw', 'bezahlt', 'anteil'],
  schmal: ['q', 'erw', 'anteil'],
};

type Zeile = PrognoseQuartalP2 & { alle?: boolean };

const ohneVorlaeufige = (r: Zeile) => r.excluded_provisional ?? 0;
const ohneWert = (r: Zeile) => r.excluded_without_value ?? 0;

const PrognoseKarte: FC = () => {
  const { data, fehler } = useLaden(fetchPrognoseGeteilt);
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('md'));
  const s = schwellenAus(data);
  // Am Handy steht das Alter in der Namenszelle — dort gilt sie als Partner von „Anteil schon bezahlt" (P2-V3 § 5).
  // Die Breite kennt erst die Spaltenwahl; der nächste Render baut die Spalten dann ohne Kopplung.
  const [schmalZuletzt, setSchmalZuletzt] = useState(false);

  const chartData = useMemo(
    () =>
      (data?.quarters ?? []).map((q) => ({
        quarter: q.quarter,
        erwartet: q.expected_value_reached_base,
        bezahlt: q.paid_revenue_with_value,
        anfos: q.anfos,
        anteil: q.reached_share ?? 0,
        jung: q.is_young ?? false,
      })),
    [data],
  );

  const sm = data?.summary;
  const summenZeile: Zeile | null = sm ? ({ ...sm, quarter: 'alle', alle: true, avg_age_days: null } as Zeile) : null;

  const baue = (sichtbar: ReadonlySet<string>, schmal: boolean): Spalte<Zeile>[] => [
    {
      key: 'q',
      label: 'Bestellquartal',
      gruppe: 'Menge',
      pflicht: true,
      sortValue: (r) => r.quarter,
      render: (r) =>
        r.alle ? (
          <>Alle Quartale</>
        ) : (
          <Box sx={{ lineHeight: 1.3 }}>
            {formatPeriode(r.quarter)}
            {/* Anfos nur, wenn ihre Spalte aus ist; das Alter IMMER, wenn das Bündel „Bisher bezahlt" fehlt — am Handy ist es der
                Partner von „Anteil schon bezahlt" (Codex 25.10.: mit Spalte „Anfos" an fiel es vorher weg). */}
            {(!sichtbar.has('anfos') || !sichtbar.has('bezahlt')) && (
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
                {[
                  !sichtbar.has('anfos') ? `${int(r.anfos)} Anfos` : null,
                  !sichtbar.has('bezahlt') && r.avg_age_base != null ? `Ø ${int(Math.round(r.avg_age_base))} Tage` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Box>
            )}
          </Box>
        ),
    },
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      gruppe: 'Menge',
      align: 'right',
      sortValue: (r) => r.anfos,
      render: (r) => <AnfosZelle n={r.anfos} vorlaeufig={r.provisional} />,
    },
    {
      key: 'erw',
      label: TERMS.heutigerAngebotswert.label,
      tip: `${TERMS.heutigerAngebotswert.tip} Ohne vorläufig ergänzte Anfos (¹) und ohne Anfos ohne Erwarteten Wert (²).`,
      einheit: '36 Monate · Summe · Ø je Anfo',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      sortValue: (r) => r.expected_value_reached_base,
      render: (r) => (
        <Box sx={{ lineHeight: 1.25 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {tEuro(r.expected_value_reached_base)}
            {ohneVorlaeufige(r) > 0 && <sup>¹</sup>}
          </Box>
          {!r.alle && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              Ø {formatEur((r.with_value_fest ?? 0) > 0 ? r.expected_value_reached_base / (r.with_value_fest ?? 1) : null, 0)}
            </Box>
          )}
          {schmal && !sichtbar.has('veraenderung') && !r.alle && (
            <Box component="span" sx={{ display: 'block', fontSize: 11 }}>
              <VeraenderungZelle v={r.veraenderung} zerlegung={false} ohneGrund="Kein Vorjahresquartal im Datenbestand." />
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'bezahlt',
      label: TERMS.umsatzNetto.label,
      tip: spaltenTip('umsatzNetto', `Über dieselben Anfos wie der Erwartete Wert.`),
      einheit: 'bezahlte Anfos · Ø Alter',
      gruppe: 'Zahlung',
      align: 'right',
      sortValue: (r) => r.paid_revenue_with_value,
      render: (r) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'flex-start' }}>
          <BezahltBuendel
            wert={`${tEuro(r.paid_revenue_with_value)}${ohneWert(r) > 0 ? '²' : ''}`}
            zahler={r.payers ?? 0}
            alterTage={r.alle || r.avg_age_base == null ? null : Math.round(r.avg_age_base)}
          />
        </Box>
      ),
    },
    {
      key: 'anteil',
      label: TERMS.anteilSchonBezahlt.label,
      tip: TERMS.anteilSchonBezahlt.tip,
      einheit: TERMS.anteilSchonBezahlt.einheit,
      gruppe: 'Zahlung',
      // Kopplung (P2-V3 § 5, Regel 10): ohne Alter daneben verleitet der Anteil zum Vergleich alt gegen jung.
      ...(schmalZuletzt ? {} : { nurMit: 'bezahlt' }),
      align: 'right',
      sortValue: (r) => (r.is_young ? null : r.reached_share),
      render: (r) => {
        if (r.alle) return <>—</>;
        if (r.is_young)
          return (
            <Tooltip title={jungTip(s.jung_anteil, s.jung_tage)} arrow placement="top">
              <Box component="span" sx={{ cursor: 'help', color: 'text.disabled' }}>
                — (jung)
              </Box>
            </Tooltip>
          );
        return (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {formatPct(r.reached_share, 0)}
            {(r.reached_share ?? 0) > 100 && <InfoTip text={TERMS.anteilSchonBezahlt.tip} />}
          </Box>
        );
      },
    },
    {
      key: 'veraenderung',
      label: TERMS.veraenderungVorjahresquartal.label,
      tip: TERMS.veraenderungVorjahresquartal.tip,
      einheit: TERMS.veraenderungVorjahresquartal.einheit,
      gruppe: 'Trend',
      align: 'right',
      render: (r) => (r.alle ? null : <VeraenderungZelle v={r.veraenderung} zerlegung={!schmal} ohneGrund="Kein Vorjahresquartal im Datenbestand." />),
    },
    {
      key: 'hoch',
      label: TERMS.hochrechnung.label,
      tip: TERMS.hochrechnung.tip,
      einheit: 'Summe, netto',
      gruppe: 'Modell',
      align: 'right',
      render: (r) => tEuro(r.projected_value),
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(new Set(), schmalZuletzt)), P2_GRUPPEN);
  const wahl = useSpaltenwahl('umsatzErwartung', meta, STANDARDS);
  const schmal = wahl.breite === 'schmal';
  useEffect(() => setSchmalZuletzt(schmal), [schmal]);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(sichtbar, schmal)
    .filter((c) => sichtbar.has(c.key))
    .map((c) => ({ ...c, summary: summenZeile ? c.render(summenZeile, { sortKey: null, sortGrund: null, ebene: 0 }) : undefined }));

  // B6c (Audit 30.10.2026): die Summenzeile trägt dieselben Marken; ihre Fußnote nennt, was der Summe fehlt — vor allem das
  // Geld der Einmalkäufe ohne Erwarteten Wert (bezahlt, aber in keiner Zeile von „Bisher bezahlt").
  const summenFussnoten = sm
    ? [
        ...(ohneVorlaeufige(sm as Zeile) > 0
          ? [`¹ Alle Quartale: ohne ${int(ohneVorlaeufige(sm as Zeile))} vorläufig ergänzte Anfos (ihr Erwarteter Wert ${tEuro(sm.excluded_provisional_value ?? 0)}).`]
          : []),
        ...(ohneWert(sm as Zeile) > 0
          ? [
              `² Alle Quartale: ohne ${int(ohneWert(sm as Zeile))} Anfos ohne Erwarteten Wert (Einmalkäufe). Sie haben ${tEuro(sm.excluded_without_value_paid ?? 0)} bezahlt (${int(sm.excluded_without_value_payers ?? 0)} Zahler); dieses Geld steht nicht in „${TERMS.umsatzNetto.label}“ der Summe, weil ihm kein Erwarteter Wert gegenübersteht.`,
            ]
          : []),
      ]
    : [];
  const fussnoten = (data?.quarters ?? [])
    .filter((q) => ohneVorlaeufige(q) > 0 || ohneWert(q) > 0)
    .reverse()
    .flatMap((q) => [
      ...(ohneVorlaeufige(q) > 0
        ? [
            `¹ ${formatPeriode(q.quarter)}: ohne ${int(ohneVorlaeufige(q))} vorläufig ergänzte Anfos (Zahlstand unbekannt; ihr Erwarteter Wert ${tEuro(q.excluded_provisional_value ?? 0)}).`,
          ]
        : []),
      ...(ohneWert(q) > 0
        ? [
            `² ${formatPeriode(q.quarter)}: ohne ${int(ohneWert(q))} Anfos ohne Erwarteten Wert (Einmalkäufe; bezahlt ${tEuro(q.excluded_without_value_paid ?? 0)}, ${int(q.excluded_without_value_payers ?? 0)} Zahler).`,
          ]
        : []),
    ])
    .concat(summenFussnoten);
  const mitJung = chartData.some((d) => d.jung);
  const basisFarbe = (id: string | number) => (id === 'erwartet' ? viz.series1Soft : viz.series1);

  return (
    <ChartCard
      title="Erwartung gegen Ist je Bestellquartal"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Je Bestellquartal: was die Anfos nach dem heutigen Wert ihrer Angebote über 36 Monate erwarten lassen (hell) und welcher Teil davon schon bezahlt ist (dunkel). Ältere Quartale hatten mehr Zeit zu zahlen. Vergleiche den Anteil deshalb nur zwischen ähnlich alten Quartalen; das Alter steht immer daneben."
      terms={[
        { label: TERMS.heutigerAngebotswert.label, tip: TERMS.heutigerAngebotswert.tip },
        { label: TERMS.anteilSchonBezahlt.label, tip: TERMS.anteilSchonBezahlt.tip },
        { label: TERMS.veraenderungVorjahresquartal.label, tip: TERMS.veraenderungVorjahresquartal.tip },
        { label: TERMS.hochrechnung.label, tip: TERMS.hochrechnung.tip },
        { label: TERMS.jung.label, tip: TERMS.jung.tip },
        { label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip },
      ]}
      source="PL-X-Bestellungen und Zahlungen, Anfo-Wert je Anfo"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <Kartenkopf>
              Bestellungen ab {data.window.von.slice(8, 10)}.{data.window.von.slice(5, 7)}.{data.window.von.slice(0, 4)} bis <DatenstandText ds={data.datenstand} /> ·
              bezahlt bis zum selben Stand · bewertet mit dem heutigen Wert der Angebote ({data.anfo_wert_stand})
            </Kartenkopf>
            <Legende
              items={[
                { label: TERMS.heutigerAngebotswert.label, color: viz.series1Soft },
                { label: TERMS.umsatzNetto.label, color: viz.series1 },
                ...(mitJung ? [{ label: 'junges Quartal (zahlt noch)', color: viz.series1, unsicher: true }] : []),
              ]}
            />
            <Box sx={{ height: 280 }}>
              <ResponsiveBar
                data={chartData as unknown as BarDatum[]}
                keys={['erwartet', 'bezahlt']}
                indexBy="quarter"
                groupMode="grouped"
                margin={{ top: 8, right: 8, bottom: 32, left: 64 }}
                padding={0.3}
                innerPadding={2}
                borderRadius={2}
                // D6: junge Quartale in ihrer Farbe mit geringer Deckkraft + Kontur (`UNSICHER`), nicht mehr übermalt.
                colors={(b) => (b.data.jung ? unsicherFuellung(basisFarbe(b.id)) : basisFarbe(b.id))}
                theme={viz.nivo}
                animate={false}
                enableLabel={false}
                enableGridY
                gridYValues={4}
                // C6 (Persona-Umbau 28.10.2026): Achse in T€ wie die Tabelle („200 T€" statt „200.000 €").
                axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4, format: (v) => `${(Number(v) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} T€` }}
                axisBottom={{
                  tickSize: 0,
                  tickPadding: 6,
                  tickValues: chartData.map((d) => d.quarter).filter((_, i, a) => breit || (a.length - 1 - i) % 3 === 0),
                  format: (v) => formatPeriode(String(v)),
                }}
                tooltip={({ indexValue, data: d }) => (
                  <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      {formatPeriode(String(indexValue))} · {int(Number(d.anfos))} Anfos{d.jung ? ' · jung' : ''}
                    </Box>
                    <TipRow color={viz.series1Soft} label={TERMS.heutigerAngebotswert.short} value={tEuro(Number(d.erwartet))} />
                    <TipRow color={viz.series1} label={TERMS.umsatzNetto.short} value={tEuro(Number(d.bezahlt))} />
                    <Box sx={{ opacity: 0.8 }}>
                      {TERMS.anteilSchonBezahlt.label}: {d.jung ? '— (jung)' : formatPct(Number(d.anteil), 0)}
                    </Box>
                  </Box>
                )}
                layers={['grid', 'axes', 'bars', unsicherKonturen((b) => (b.data.data.jung ? basisFarbe(b.data.id) : null))]}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <SortableTable
                rows={[...data.quarters].reverse() as Zeile[]}
                columns={spalten}
                rowKey={(r) => r.quarter}
                maxHeight={null}
                eng={schmal}
                summaryLabel="Alle Quartale"
                namensEtikett={(r) => (r.is_young ? <JungEtikett jungAnteil={s.jung_anteil} jungTage={s.jung_tage} /> : null)}
              />
            </Box>
            {fussnoten.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 1, p: 0, listStyle: 'none', fontSize: 12, color: 'text.secondary', lineHeight: 1.6 }}>
                {fussnoten.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </Box>
            )}
            <Hinweis>
              Euro netto. Der Erwartete Wert jeder Anfo ist der heutige Wert ihres Angebots, ohne Angebotswert der Einzelwert ihres Produkts — auch
              für alte Quartale. „{TERMS.anteilSchonBezahlt.label}“ teilt das bisher Bezahlte derselben Anfos dadurch; junge Quartale (grau) haben
              noch wenig zahlen können. Über 100 %: Diese Anfos haben besser gezahlt, als der heutige Wert ihres Angebots annimmt — für sie ist
              der Erwartete Wert zu niedrig.{' '}
              {data.summary.projected_value === null
                ? 'Die Hochrechnung aus den bisherigen Zahlungen gibt es nur zum heutigen Stand.'
                : 'Die Hochrechnung aus den bisherigen Zahlungen steht nur im Spaltenmenü: in alten Quartalen ist sie fast nur echtes Geld, in jungen fast nur Angebotswert.'}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default PrognoseKarte;
